import { RARITY_IDS } from "@highmarch/domain";
import { WellOpts, burstHtml, priceButtonHtml, slotTileHtml } from "../components";
import { artUrl } from "../deals/dealsTiles";
import type { ItemTile } from "../deals/dealsModel";
import { CURRENCY_NAMES, SlotTile } from "./shopsModel";
import "../styles/offerPolish.css";

/**
 * The shops' slots in the design-system v2 markup (ui/components.ts): each item in its well — the rarity jewel for
 * chests and gear, the neutral navy well for gems, gold, speed-ups and skins — in a `slotTileHtml` slot
 * with the gold price button, the discount burst, "Left: n", the Sold out stamp or the VIP lock. The smoke hooks
 * (`.stile.s-<state>[data-i]`, `.feat`) sit beside the `k-` classes. Markup only.
 */

/** Item kinds whose well wears the rarity jewel (STYLE rule 4): the rest sit in the neutral navy well. */
const RARITY_KINDS: ReadonlySet<string> = new Set(["chest", "luckyChest", "gear"]);

const escAttr = (s: string): string => s.replace(/"/g, "&quot;");

/** The rarity index an item tile's well wears, or null for the neutral well. */
export function tileRarity(t: ItemTile): number | null {
  if (!RARITY_KINDS.has(t.kind)) return t.tier ?? null;
  const i = (RARITY_IDS as readonly string[]).indexOf(t.tone);
  return i >= 0 ? i : (t.tier ?? null);
}

/** An offer item's well: its art, count and cap (a speed-up's length), and the "?" when its contents are rolled. */
export function itemWellOpts(t: ItemTile, size: WellOpts["size"] = "md"): WellOpts {
  return { art: artUrl(t.icon), rarity: tileRarity(t), size, count: t.count, cap: t.top, info: t.random || t.needsSlot };
}

/** One shop slot: the well, "Left: n", the gold price (the amount red when short), Sold out or the VIP lock. */
export function slotHtml(t: SlotTile): string {
  const state = t.state === "sold" ? "sold" : t.state === "locked" ? "locked" : "ok";
  const says = t.state === "sold" ? "sold out" : t.state === "locked" ? `opens at ${t.lock}` : `${t.price} ${CURRENCY_NAMES[t.currency]}`;
  // MP-08: the Merchant's free item: a gold "Free" with no currency, and a FREE tag where a discount burst would sit
  const price = priceButtonHtml({ currency: t.free ? "real" : t.currency, amount: t.price, short: t.state === "short", className: `sbuy${t.free ? " s-free" : ""}`, data: `aria-label="${escAttr(`${t.tile.name}, ${t.free && t.state !== "sold" ? "free today" : says}`)}"` });
  const corner = t.free && t.state !== "sold" ? `<span class="k-free">FREE</span>` : t.badge ? burstHtml(t.badge, "off") : "";
  return slotTileHtml({
    well: itemWellOpts(t.tile), name: t.tile.name, left: t.left, price, burst: corner, state, lock: t.lock,
    featured: t.featured, className: `stile s-${t.state}${t.featured ? " feat" : ""} tap`, data: `data-i="${t.index}"`,
  });
}
